import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, InfoField, Field, ErrorAlert } from "src/components";
import { AccountBalance, Bank } from "src/api";
import { formatNumberToLocale, onlyNumbers } from "src/utils";
import { EWithdrawalStep } from "src/types";
import { WithdrawalStep } from "src/hooks";

interface IWithdrawalConfirmationStepProps {
  amount: string;
  paymentFee: number;
  balance: AccountBalance;
  selectedBank: Bank | null;
  accountNumber: string;
  handleStepChange: (step: EWithdrawalStep, isBackNavigation?: boolean) => void;
  withdrawalStep: WithdrawalStep;
  isMfaRequired: boolean;
  mfaCode: string;
  setMfaCode: (code: string) => void;
}

const WithdrawalConfirmationStep: React.FC<IWithdrawalConfirmationStepProps> = ({
  amount,
  paymentFee,
  balance,
  selectedBank,
  accountNumber,
  handleStepChange,
  withdrawalStep,
  isMfaRequired,
  mfaCode,
  setMfaCode,
}): JSX.Element => {
  const { t } = useTranslation();
  const [mfaCodeTouched, setMfaCodeTouched] = useState(false);
  const {
    currency: { currency_id, precision },
  } = balance;
  const { stepError, stepLoading } = withdrawalStep;
  const mfaErrors = [
    ...(isMfaRequired && !mfaCode ? [t("WITHDRAWAL_ERRORS.MFA_CODE_REQUIRED")] : []),
    ...(isMfaRequired && mfaCode.trim().length < 6 ? [t("WITHDRAWAL_ERRORS.MFA_CODE_MUST_BE_6_DIGITS")] : []),
  ];
  const submitDisabled = mfaErrors.length > 0;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitDisabled) return;
    handleStepChange(EWithdrawalStep.InitiateWithdrawal);
  };

  const handleMfaCodeChange = (value: string) => {
    if (onlyNumbers(value)) {
      setMfaCode(value);
    }
  };

  return (
    <form className="withdrawal-form" onSubmit={handleSubmit}>
      <div className="withdrawal-form-section">
        <InfoField label={t("WITHDRAWAL_STEPS.AMOUNT")} value={`${formatNumberToLocale(Number(amount), precision)} ${currency_id}`} />

        <InfoField label={t("WITHDRAWAL_STEPS.FEE")} value={`${formatNumberToLocale(paymentFee, precision)} ${currency_id}`} />

        <InfoField
          label={t("WITHDRAWAL_STEPS.YOU_WILL_RECEIVE")}
          value={`${formatNumberToLocale(Number(amount) - Number(paymentFee), precision)} ${currency_id}`}
        />
      </div>

      <div className="withdrawal-form-section">
        <InfoField label={t("WITHDRAWAL_STEPS.BANK")} value={selectedBank?.bankGroupName || "N/A"} />

        <InfoField label={t("WITHDRAWAL_STEPS.ACCOUNT_NUMBER")} value={accountNumber} />
      </div>

      {isMfaRequired && (
        <Field
          label={t("WITHDRAWAL_STEPS.MFA_CODE")}
          value={mfaCode}
          onChange={handleMfaCodeChange}
          placeholder={t("WITHDRAWAL_STEPS.ENTER_YOUR_MFA_CODE")}
          disabled={stepLoading}
          error={mfaCodeTouched ? mfaErrors[0] : undefined}
          onBlur={() => setMfaCodeTouched(true)}
          maxLength={6}
        />
      )}

      <ErrorAlert message={stepError} />

      <div className="withdrawal-form-buttons">
        <Button
          title={t("WITHDRAWAL_STEPS.BACK")}
          className="withdrawal-button"
          type="button"
          onClick={() => handleStepChange(EWithdrawalStep.BankSelection, true)}
          disabled={stepLoading}
          secondary
        />
        <Button
          title={t("WITHDRAWAL_STEPS.SUBMIT")}
          className="withdrawal-button"
          type="submit"
          pending={stepLoading}
          disabled={submitDisabled}
          tooltipMessage={mfaErrors[0]}
        />
      </div>
    </form>
  );
};

export default WithdrawalConfirmationStep;
